
import React, { useState } from 'react';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { ArrowUpRight, ArrowDownRight, DollarSign, Users, Layers, Activity, ChevronDown, ShoppingBag, AlertCircle, CheckCircle2, Calendar, CheckSquare, Plus } from 'lucide-react';
import { CASHFLOW_DATA } from '../constants';
import { ViewState, ClientFilterType, Client } from '../types';

interface DashboardProps {
  clients?: Client[];
  onNavigate?: (view: ViewState, filter?: ClientFilterType) => void;
}

// Mini Calendar / To Do List Widget
const DashboardToDoList: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
    // Mock Data
    const tasks = [
        { day: 2, count: 2 },
        { day: 5, count: 1 },
        { day: 10, count: 1 },
        { day: 15, count: 2 },
        { day: 22, count: 1 },
    ];
    
    // 5 Rows x 7 Cols = 35 days grid mock
    const days = Array.from({ length: 35 }, (_, i) => i + 1);
    const today = 2; // Mock Today

    return (
        <div 
            onClick={onNavigate}
            className="flex flex-col h-full cursor-pointer group"
        >
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-slate-900 flex items-center">
                    <CheckSquare size={18} className="mr-2 text-slate-600" />
                    My To-Do's
                </h3>
                <span className="text-xs text-slate-400 group-hover:text-slate-600 transition-colors">View Calendar &rarr;</span>
            </div>

            <div className="flex-1 bg-slate-50/50 rounded-xl border border-slate-200/50 p-3 overflow-hidden">
                {/* Header Days */}
                <div className="grid grid-cols-7 mb-2 text-center">
                    {['M','T','W','T','F','S','S'].map((d,i) => (
                        <span key={i} className="text-[10px] font-bold text-slate-400">{d}</span>
                    ))}
                </div>
                
                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-1.5">
                    {days.map(day => {
                        const hasTask = tasks.find(t => t.day === day);
                        const isToday = day === today;
                        const isCurrentMonth = day <= 31;
                        
                        if (!isCurrentMonth) return <div key={day} />; // Spacer

                        return (
                            <div 
                                key={day} 
                                className={`
                                    aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all duration-200
                                    ${isToday ? 'bg-slate-900 text-white shadow-md shadow-slate-200 scale-105 z-10' : 'bg-white border border-slate-100 text-slate-500 hover:border-slate-300'}
                                `}
                            >
                                <span className="text-[10px] font-medium leading-none">{day}</span>
                                {hasTask && (
                                    <div className="flex space-x-0.5 mt-0.5">
                                        {Array.from({length: Math.min(hasTask.count, 2)}).map((_, i) => (
                                            <div key={i} className={`w-1 h-1 rounded-full ${isToday ? 'bg-white' : 'bg-slate-400'}`}></div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500 px-1">
                 <span>7 Tasks pending</span>
                 <div className="flex -space-x-2">
                     <div className="w-5 h-5 rounded-full bg-slate-200 border border-white"></div>
                     <div className="w-5 h-5 rounded-full bg-slate-300 border border-white"></div>
                     <div className="w-5 h-5 rounded-full bg-slate-200 border border-white flex items-center justify-center text-[8px] font-bold text-slate-700">+3</div>
                 </div>
            </div>
        </div>
    );
};


const Dashboard: React.FC<DashboardProps> = ({ clients = [], onNavigate }) => {
  const [invoiceFilter, setInvoiceFilter] = useState<'This Week' | 'This Month'>('This Week');

  // Calculate dynamic stats from props
  const activeClientsCount = clients.filter(c => c.type === 'Client' && c.status === 'Active').length;
  // In our model, 'Active GMC's' maps to Active status
  const activeGMCsCount = clients.filter(c => c.status === 'Active').length;
  // 'Non-active GMC's' maps to Attention status
  const nonActiveGMCsCount = clients.filter(c => c.status === 'Attention').length;


  // Mock data for the filterable card
  const invoiceData = {
    'This Week': { value: '€ 8,450', trend: '+12%', trendUp: true },
    'This Month': { value: '€ 34,200', trend: '+5%', trendUp: true },
  };

  const currentInvoiceData = invoiceData[invoiceFilter];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Top KPI Blocks */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* Card 1: Invoice Amount (Filterable) */}
        <div className="p-6 rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300">
          <div className="flex justify-between items-start mb-4">
            <div className="p-2 bg-blue-50/80 text-blue-600 rounded-lg">
              <DollarSign size={20} strokeWidth={1.5} />
            </div>
            <div className="relative group">
                <button className="flex items-center space-x-1 text-xs font-medium text-slate-500 hover:text-slate-800 bg-white/50 px-2 py-1 rounded-md transition-colors">
                    <span>{invoiceFilter}</span>
                    <ChevronDown size={12} />
                </button>
                {/* Simple dropdown mock */}
                <div className="absolute right-0 top-full mt-1 w-24 bg-white rounded-lg shadow-lg border border-slate-100 py-1 hidden group-hover:block z-10">
                    <button onClick={() => setInvoiceFilter('This Week')} className="w-full text-left px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">This Week</button>
                    <button onClick={() => setInvoiceFilter('This Month')} className="w-full text-left px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">This Month</button>
                </div>
            </div>
          </div>
          <div>
            <h3 className="text-slate-500 text-sm font-light mb-1">Invoice Amount</h3>
            <div className="flex items-end space-x-2">
                <p className="text-2xl font-semibold text-slate-800 tracking-tight">{currentInvoiceData.value}</p>
                <span className={`flex items-center text-xs font-medium mb-1.5 ${currentInvoiceData.trendUp ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {currentInvoiceData.trendUp ? <ArrowUpRight size={12} className="mr-0.5" /> : <ArrowDownRight size={12} className="mr-0.5" />}
                    {currentInvoiceData.trend}
                </span>
            </div>
          </div>
        </div>

        {/* Card 2: Active Customers */}
        <div 
            onClick={() => onNavigate?.('clients', 'active')}
            className="p-6 rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="flex justify-between items-start mb-4">
            <div className="p-2 bg-indigo-50/80 text-indigo-600 rounded-lg">
              <Users size={20} strokeWidth={1.5} />
            </div>
            <span className="flex items-center text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                <ArrowUpRight size={12} className="mr-1" />
                +2
            </span>
          </div>
          <div>
            <h3 className="text-slate-500 text-sm font-light mb-1">Active Customers</h3>
            <p className="text-2xl font-semibold text-slate-800 tracking-tight">{activeClientsCount}</p>
          </div>
        </div>

        {/* Card 3: Active GMC's */}
        <div 
            onClick={() => onNavigate?.('clients', 'active')}
            className="p-6 rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="flex justify-between items-start mb-4">
            <div className="p-2 bg-slate-100/80 text-slate-700 rounded-lg">
              <ShoppingBag size={20} strokeWidth={1.5} />
            </div>
            <div className="p-1 rounded-full bg-slate-200/50 text-slate-700">
                <CheckCircle2 size={14} />
            </div>
          </div>
          <div>
            <h3 className="text-slate-900 text-sm font-medium mb-1">Active GMC's</h3>
            <p className="text-2xl font-semibold text-slate-900 tracking-tight">{activeGMCsCount}</p>
          </div>
        </div>

        {/* Card 4: Non-active GMC's (Light Red) */}
        <div 
            onClick={() => onNavigate?.('clients', 'attention')}
            className="p-6 rounded-2xl bg-red-50/60 backdrop-blur-md border border-red-100/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-all duration-300 cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
        >
          <div className="flex justify-between items-start mb-4">
            <div className="p-2 bg-red-100/80 text-red-700 rounded-lg">
              <AlertCircle size={20} strokeWidth={1.5} />
            </div>
             <div className="p-1 rounded-full bg-red-200/50 text-red-700">
                <Activity size={14} />
            </div>
          </div>
          <div>
            <h3 className="text-red-800/70 text-sm font-medium mb-1">Non-active GMC's</h3>
            <p className="text-2xl font-semibold text-red-900 tracking-tight">{nonActiveGMCsCount}</p>
          </div>
        </div>

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cashflow Chart */}
        <div className="lg:col-span-2 p-6 rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow-sm animate-fade-in">
          <h3 className="text-lg font-medium text-slate-800 mb-6">Cashflow Forecast</h3>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={CASHFLOW_DATA}>
                <defs>
                  <linearGradient id="colorCashIn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={10} />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(4px)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  itemStyle={{ color: '#334155', fontSize: '12px' }}
                />
                <Area type="monotone" dataKey="expected" stackId="1" stroke="#cbd5e1" fill="transparent" strokeDasharray="3 3" />
                <Area type="monotone" dataKey="cashIn" stackId="2" stroke="#10b981" fill="url(#colorCashIn)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Replaced Project Status with To Do List Widget */}
        <div className="p-6 rounded-2xl bg-white/60 backdrop-blur-md border border-white/40 shadow-sm animate-fade-in">
            <DashboardToDoList onNavigate={() => onNavigate?.('todo')} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
